import * as THREE from 'three';
import { type Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';
import {
  LabTextures,
  wallVariantFor,
  wallVariantForFace,
  floorVariantFor,
  floorOrientFor,
  wallTopOrientFor,
  wallFaceFlipFor,
} from './LabyrinthTextures';
import {
  applyStylizedShading,
  type LightmapBindings,
  type WallPulseBindings,
} from './StylizedShading';

export type { LightmapBindings, WallPulseBindings };

const WALL_HEIGHT = TILE_SIZE;
// Tints sit in the upper-mid range so ambient+lightmap can drive the surface
// into highlight territory; ACES tone mapping then crunches them into a
// punchy contrasty roll-off without clipping.
const FLOOR_TINT   = 0xc0c0c0;
const CEILING_TINT = 0x969696;
const WALL_TINT    = 0xb4b4b4;

const isWallTile = (lab: Labyrinth, x: number, y: number): boolean => {
  if (x < 0 || x >= lab.width || y < 0 || y >= lab.height) return true;
  return lab.tiles[y]![x] === Tile.Wall;
};

export class LabyrinthMesh {
  readonly group = new THREE.Group();

  private readonly floorGeo = new THREE.BufferGeometry();
  private readonly ceilGeo  = new THREE.BufferGeometry();
  private readonly wallGeo  = new THREE.BufferGeometry();

  constructor(lab: Labyrinth, lightmap: LightmapBindings, wallPulse: WallPulseBindings) {
    const half = TILE_SIZE / 2;

    const floorPos: number[] = [];
    const floorNrm: number[] = [];
    const floorUV:  number[] = [];
    const floorIdx: number[] = [];
    const ceilPos: number[] = [];
    const ceilNrm: number[] = [];
    const ceilUV:  number[] = [];
    const ceilIdx: number[] = [];
    const wallPos: number[] = [];
    const wallNrm: number[] = [];
    const wallUV:  number[] = [];
    const wallIdx: number[] = [];

    const nWallVariants  = LabTextures.wallVariantCount;
    const nFloorVariants = LabTextures.floorVariantCount;

    // Base corners are listed in vertex order (BL,BR,TR,TL for top faces;
    // BL,BR,TR,TL viewed from outside for wall faces). Cycling the array by
    // `rot` steps applies a 90° rotation per step; `flip` mirrors horizontally
    // by swapping opposite pairs. 4 rotations × 2 mirrors = 8 orientations.
    const pushOrientedUV = (
      uv: number[],
      corners: [number, number][],
      orientation: number,
    ): void => {
      const rot = orientation & 3;
      const flip = (orientation & 4) !== 0;
      let c: [number, number][] = corners;
      for (let i = 0; i < rot; i++) c = [c[1]!, c[2]!, c[3]!, c[0]!];
      if (flip) c = [c[1]!, c[0]!, c[3]!, c[2]!];
      for (const [u, v] of c) uv.push(u, v);
    };

    const pushTopAtlasUV = (
      uv: number[], variant: number, total: number, orientation: number,
    ): void => {
      const u0 = variant / total;
      const u1 = (variant + 1) / total;
      pushOrientedUV(uv, [[u0, 1], [u1, 1], [u1, 0], [u0, 0]], orientation);
    };
    const pushWallAtlasUV = (uv: number[], variant: number, flip: boolean): void => {
      const u0 = variant / nWallVariants;
      const u1 = (variant + 1) / nWallVariants;
      // Mirror-only (no rotation) — vertical structure must stay vertical.
      pushOrientedUV(uv, [[u0, 0], [u1, 0], [u1, 1], [u0, 1]], flip ? 4 : 0);
    };

    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        const cx = (x + 0.5) * TILE_SIZE;
        const cz = (y + 0.5) * TILE_SIZE;
        const isWall = isWallTile(lab, x, y);

        if (!isWall) {
          const vStart = floorPos.length / 3;
          floorPos.push(
            cx - half, 0, cz - half,
            cx + half, 0, cz - half,
            cx + half, 0, cz + half,
            cx - half, 0, cz + half,
          );
          for (let k = 0; k < 4; k++) floorNrm.push(0, 1, 0);
          pushTopAtlasUV(
            floorUV, floorVariantFor(x, y), nFloorVariants, floorOrientFor(x, y),
          );
          floorIdx.push(vStart, vStart + 1, vStart + 2, vStart, vStart + 2, vStart + 3);
          continue;
        }

        const topVariant = wallVariantFor(x, y);

        const cStart = ceilPos.length / 3;
        ceilPos.push(
          cx - half, WALL_HEIGHT, cz - half,
          cx + half, WALL_HEIGHT, cz - half,
          cx + half, WALL_HEIGHT, cz + half,
          cx - half, WALL_HEIGHT, cz + half,
        );
        for (let k = 0; k < 4; k++) ceilNrm.push(0, 1, 0);
        pushTopAtlasUV(ceilUV, topVariant, nWallVariants, wallTopOrientFor(x, y));
        // Wind ceiling CCW when viewed from above (+Y) — reversed from floor.
        ceilIdx.push(cStart, cStart + 2, cStart + 1, cStart, cStart + 3, cStart + 2);

        // dir: 0=north(-z), 1=south(+z), 2=west(-x), 3=east(+x)
        if (!isWallTile(lab, x, y - 1)) {
          const i = wallPos.length / 3;
          wallPos.push(
            cx - half, 0,           cz - half,
            cx + half, 0,           cz - half,
            cx + half, WALL_HEIGHT, cz - half,
            cx - half, WALL_HEIGHT, cz - half,
          );
          for (let k = 0; k < 4; k++) wallNrm.push(0, 0, -1);
          pushWallAtlasUV(wallUV, wallVariantForFace(x, y, 0), wallFaceFlipFor(x, y, 0));
          wallIdx.push(i, i + 2, i + 1, i, i + 3, i + 2);
        }
        if (!isWallTile(lab, x, y + 1)) {
          const i = wallPos.length / 3;
          wallPos.push(
            cx - half, 0,           cz + half,
            cx + half, 0,           cz + half,
            cx + half, WALL_HEIGHT, cz + half,
            cx - half, WALL_HEIGHT, cz + half,
          );
          for (let k = 0; k < 4; k++) wallNrm.push(0, 0, 1);
          pushWallAtlasUV(wallUV, wallVariantForFace(x, y, 1), wallFaceFlipFor(x, y, 1));
          wallIdx.push(i, i + 1, i + 2, i, i + 2, i + 3);
        }
        if (!isWallTile(lab, x - 1, y)) {
          const i = wallPos.length / 3;
          wallPos.push(
            cx - half, 0,           cz - half,
            cx - half, 0,           cz + half,
            cx - half, WALL_HEIGHT, cz + half,
            cx - half, WALL_HEIGHT, cz - half,
          );
          for (let k = 0; k < 4; k++) wallNrm.push(-1, 0, 0);
          pushWallAtlasUV(wallUV, wallVariantForFace(x, y, 2), wallFaceFlipFor(x, y, 2));
          wallIdx.push(i, i + 1, i + 2, i, i + 2, i + 3);
        }
        if (!isWallTile(lab, x + 1, y)) {
          const i = wallPos.length / 3;
          wallPos.push(
            cx + half, 0,           cz - half,
            cx + half, 0,           cz + half,
            cx + half, WALL_HEIGHT, cz + half,
            cx + half, WALL_HEIGHT, cz - half,
          );
          for (let k = 0; k < 4; k++) wallNrm.push(1, 0, 0);
          pushWallAtlasUV(wallUV, wallVariantForFace(x, y, 3), wallFaceFlipFor(x, y, 3));
          wallIdx.push(i, i + 2, i + 1, i, i + 3, i + 2);
        }
      }
    }

    this.floorGeo.setAttribute('position', new THREE.Float32BufferAttribute(floorPos, 3));
    this.floorGeo.setAttribute('normal',   new THREE.Float32BufferAttribute(floorNrm, 3));
    this.floorGeo.setAttribute('uv',       new THREE.Float32BufferAttribute(floorUV, 2));
    this.floorGeo.setIndex(floorIdx);

    this.ceilGeo.setAttribute('position', new THREE.Float32BufferAttribute(ceilPos, 3));
    this.ceilGeo.setAttribute('normal',   new THREE.Float32BufferAttribute(ceilNrm, 3));
    this.ceilGeo.setAttribute('uv',       new THREE.Float32BufferAttribute(ceilUV, 2));
    this.ceilGeo.setIndex(ceilIdx);

    this.wallGeo.setAttribute('position', new THREE.Float32BufferAttribute(wallPos, 3));
    this.wallGeo.setAttribute('normal',   new THREE.Float32BufferAttribute(wallNrm, 3));
    this.wallGeo.setAttribute('uv',       new THREE.Float32BufferAttribute(wallUV, 2));
    this.wallGeo.setIndex(wallIdx);

    const floorMat = new THREE.MeshBasicMaterial({
      color: FLOOR_TINT, map: LabTextures.floorAtlas,
      side: THREE.DoubleSide,
    });
    const ceilMat = new THREE.MeshBasicMaterial({
      color: CEILING_TINT, map: LabTextures.wallAtlas,
      side: THREE.DoubleSide,
    });
    const wallMat = new THREE.MeshBasicMaterial({
      color: WALL_TINT, map: LabTextures.wallAtlas,
      side: THREE.DoubleSide,
    });
    // Floor / ceiling face straight up/down → wall-tint is a no-op on them, but
    // keep the flag consistent so every map surface shares one shader variant.
    // Lightmap sample offsets by face normal (walls pick up adjacent floor tile).
    applyStylizedShading(floorMat, {
      lightmap, lightmapNormalOffset: true, keyLightStrength: 0.34, wallTint: true,
    });
    applyStylizedShading(ceilMat, {
      lightmap, lightmapNormalOffset: true, keyLightStrength: 0.22, wallTint: true,
      wallPulse,
    });
    applyStylizedShading(wallMat, {
      lightmap, lightmapNormalOffset: true, keyLightStrength: 0.42, wallTint: true,
      wallPulse,
    });

    this.group.add(new THREE.Mesh(this.floorGeo, floorMat));
    this.group.add(new THREE.Mesh(this.ceilGeo,  ceilMat));
    this.group.add(new THREE.Mesh(this.wallGeo,  wallMat));
  }

  dispose(): void {
    this.group.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach(m => m.dispose());
      else if (mat) mat.dispose();
    });
  }
}
